import React, { useEffect, useRef } from "react";
import clsx from "clsx";
import { Listbox } from "@headlessui/react";
import { BeakerIcon, GlobeAltIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { Button } from "@truevault/aquilo";
import {
  CONSENT_ENVIRONMENT_LABELS,
  CONSENT_ENVIRONMENTS,
  ConsentEnvironment,
} from "./consentEnvironment";

interface ConsentEnvironmentHeaderProps {
  environment: ConsentEnvironment;
  onEnvironmentChange: (environment: ConsentEnvironment) => void;
  websiteDomain?: string | null;
  hasUnpublishedChanges: boolean;
  // False until this environment has been published once.
  hasSavedSettings: boolean;
  isPublishing: boolean;
  // False when there's nothing to publish yet.
  canPublish: boolean;
  onPublish: () => void;
  // Shown at the end of the mode bar, e.g. a link to copy the other environment's settings.
  modeBarAction?: React.ReactNode;
  // Focus the environment switcher when the header mounts.
  autoFocusSwitcher?: boolean;
  // This environment's saved settings couldn't be loaded.
  loadFailed?: boolean;
}

const EnvironmentDot = ({
  environment,
  className,
}: {
  environment: ConsentEnvironment;
  className?: string;
}) => (
  <span
    aria-hidden="true"
    className={clsx(
      "h-[8px] w-[8px] shrink-0 rounded-full",
      environment === "production" ? "bg-emerald-500" : "bg-amber-500",
      className,
    )}
  />
);

/**
 * Sticky header for the Consent Banner tab: which environment you're editing, whether it has unpublished changes,
 * and a publish button that names its target. The mode bar underneath spells out what publishing will affect.
 */
export const ConsentEnvironmentHeader = React.forwardRef<
  HTMLDivElement,
  ConsentEnvironmentHeaderProps
>(
  (
    {
      environment,
      onEnvironmentChange,
      websiteDomain,
      hasUnpublishedChanges,
      hasSavedSettings,
      isPublishing,
      canPublish,
      onPublish,
      modeBarAction,
      autoFocusSwitcher,
      loadFailed,
    },
    ref,
  ) => {
    const switcherRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
      if (autoFocusSwitcher) {
        switcherRef.current?.focus();
      }
    }, [autoFocusSwitcher]);

    const isProduction = environment === "production";
    const website = websiteDomain ?? "your website";

    const descriptions: Record<ConsentEnvironment, string> = {
      production: `${websiteDomain ?? "Your website"} · Your live site`,
      staging: "Your staging site · For testing",
    };

    let status: React.ReactNode = null;
    if (loadFailed) {
      status = (
        <span className="text-[13px] leading-[18px] text-red-700">
          {isProduction ? "Couldn't load settings" : "Couldn't load staging settings"}
        </span>
      );
    } else if (hasUnpublishedChanges) {
      status = (
        <span className="inline-flex h-[24px] items-center rounded-full bg-blue-50 px-[10px] text-[12px] font-medium leading-[16px] text-blue-800 ring-1 ring-inset ring-blue-200">
          Unpublished changes
        </span>
      );
    } else if (hasSavedSettings) {
      status = (
        <span className="inline-flex items-center gap-[6px] text-[13px] leading-[18px] text-neutral-600">
          <CheckCircleIcon className="h-[16px] w-[16px] text-emerald-600" />
          All changes published
        </span>
      );
    } else {
      status = (
        <span className="text-[13px] leading-[18px] text-neutral-600">
          {isProduction ? "Not published yet" : "Not published to staging yet"}
        </span>
      );
    }

    return (
      <div
        ref={ref}
        className="sticky top-[var(--consent-sticky-top,0px)] z-20 rounded-xl border border-solid border-neutral-200 bg-white shadow-[0px_4px_12px_0px_rgba(26,23,38,0.08)]"
      >
        <div className="flex flex-wrap items-center justify-between gap-x-[16px] gap-y-[12px] px-[16px] py-[12px]">
          <Listbox
            as="div"
            value={environment}
            onChange={onEnvironmentChange}
            className="flex items-center gap-[12px]"
            // Switching mid-publish would remount the form while it is still saving.
            disabled={isPublishing}
          >
            <Listbox.Label className="text-[12px] font-medium uppercase leading-[16px] tracking-[0.06em] text-neutral-600">
              Environment
            </Listbox.Label>
            <div className="relative">
              <Listbox.Button
                ref={switcherRef}
                className="flex h-[36px] min-w-[176px] cursor-pointer items-center gap-[8px] rounded-md border border-solid border-neutral-300 bg-white pl-[12px] pr-[10px] text-[14px] font-medium leading-[20px] text-neutral-900 hover:border-neutral-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <EnvironmentDot environment={environment} />
                {CONSENT_ENVIRONMENT_LABELS[environment]}
                <ChevronDownIcon className="ml-auto h-[20px] w-[20px] text-neutral-500" />
              </Listbox.Button>
              <Listbox.Options className="absolute left-0 top-[calc(100%+6px)] z-30 m-0 w-[330px] list-none rounded-[10px] border border-solid border-neutral-200 bg-white p-[6px] shadow-lg focus:outline-none">
                {CONSENT_ENVIRONMENTS.map((option) => (
                  <Listbox.Option
                    key={option}
                    value={option}
                    className={({ active }) =>
                      clsx(
                        "flex cursor-pointer items-start gap-[10px] rounded-md p-[10px]",
                        active && "bg-neutral-100",
                      )
                    }
                  >
                    {({ selected }) => (
                      <>
                        <EnvironmentDot environment={option} className="mt-[6px]" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[14px] font-medium leading-[20px] text-neutral-900">
                            {CONSENT_ENVIRONMENT_LABELS[option]}
                          </span>
                          <span className="block text-[12px] leading-[16px] text-neutral-600">
                            {descriptions[option]}
                          </span>
                        </span>
                        {selected && <CheckIcon className="h-[20px] w-[20px] text-primary-600" />}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>

          <div className="flex flex-wrap items-center gap-[12px]">
            {status}
            <Button
              color={hasUnpublishedChanges ? "primary" : "tertiary"}
              customSizeClassname="h-[36px] text-[14px]"
              disabled={isPublishing || !canPublish}
              onClick={onPublish}
            >
              {isPublishing
                ? "Publishing…"
                : isProduction
                ? "Publish to production"
                : "Publish to staging"}
            </Button>
          </div>
        </div>

        <div
          className={clsx(
            "flex items-center gap-[10px] rounded-b-[11px] border-0 border-t border-solid px-[16px] py-[10px] text-[14px] leading-[20px]",
            isProduction
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-amber-200 bg-amber-50 text-amber-800",
          )}
        >
          {isProduction ? (
            <GlobeAltIcon className="h-[20px] w-[20px] shrink-0" />
          ) : (
            <BeakerIcon className="h-[20px] w-[20px] shrink-0" />
          )}
          <p role="status" className="m-0 min-w-0 flex-1">
            {isProduction ? (
              <>
                You're editing <strong className="font-semibold">production</strong>. Published
                changes go live on {website}.
              </>
            ) : (
              <>
                You're editing <strong className="font-semibold">staging</strong>. Publishing saves
                these settings to your staging script only, so visitors to your production site
                won't see them.
              </>
            )}
          </p>
          {modeBarAction && (
            <div className="flex shrink-0 flex-wrap items-center gap-x-[16px] gap-y-[4px]">
              {modeBarAction}
            </div>
          )}
        </div>
      </div>
    );
  },
);
